import { Buffer } from 'buffer';
import { ifElse, pathEq, pathOr } from 'ramda';
import { createPipe, prop } from 'remeda';
import { EventStreamMarshaller } from '@aws-sdk/eventstream-marshaller';
import { fromUtf8, toUtf8 } from '@aws-sdk/util-utf8-node';
import { convertAudioToBinaryMessage } from '../lib/audioUtils';
import Connection from '../lib/Connection';
import { CustomEventTarget } from '../lib/CustomEventTarget';
import MicStream from '../lib/MicStream';
import type { AWSTranscribeResponse, Config, ListenerCallback } from '../types/shared';
import { AWSSpeechRecognitionEvent } from '../types/shared';

type requiredConfigs = Pick<Config, 'signedUrlProvider'>;
type optionalConfigs = Omit<Config, 'signedUrlProvider'>;
export type configArgs = requiredConfigs & Partial<optionalConfigs>;

class AWSRecognizer extends CustomEventTarget implements SpeechRecognition {
  /** in case future recognizers are built in the future (e.g. Azure) */
  static type = 'AWS';

  /** true if the library is supported by the currenly browser */
  static isSupported = !!navigator?.mediaDevices?.getUserMedia;

  /** polyfill-specific config */
  public config: Config;

  /** if the library is currently capturing/transcribing audio */
  public listening = false;

  /** the langage (default en-US) */
  public lang: Config['lang'];

  /** whether to continously transribe audio until .stop() is called */
  public continuous: boolean;

  public processLocally = false;

  static available(_options: SpeechRecognitionOptions): Promise<AvailabilityStatus> {
    return Promise.resolve('unavailable');
  }

  static install(_options: SpeechRecognitionOptions): Promise<boolean> {
    return Promise.resolve(false);
  }

  /** a proxy for new AWSRecognizer(config) */
  static create(config: configArgs): typeof SpeechRecognition {
    return class AWSRecognizerWithConfig extends AWSRecognizer {
      constructor() {
        super(config);
      }
    };
  }

  constructor(config: configArgs) {
    super();
    if (!config.signedUrlProvider)
      throw new Error(
        'Could not create AWS recognizer: missing configuration, see: https://github.com/ceuk/speech-recognition-aws-polyfill#configuration'
      );
    const defaults: optionalConfigs = {
      sampleRate: 12000,
      lang: 'en-US',
      continuous: false,
    };
    this.config = Object.assign(defaults, config);
    this.lang = this.config.lang;
    this.continuous = this.config.continuous;
  }

  /** start capturing/transcribing audio */
  start() {
    if (this.listening) return;

    this.dispatchEvent(new Event('start'));
    navigator.mediaDevices
      .getUserMedia({ audio: true, video: false })
      .then(this.establishConnection.bind(this))
      .catch((err) => {
        this.emitError(err);
      });
  }

  /** stop capturing and return any final transcriptions */
  public stop() {
    const micStream = MicStream.getInstance();
    if (micStream) {
      if (micStream.removeAllListeners) {
        micStream.removeAllListeners('data');
      }
      micStream.end();
    }
    const connection = Connection.getInstance();
    if (connection) {
      connection.close();
    }
    this.listening = false;
    this.dispatchEvent(new Event('audioend'));
    this.dispatchEvent(new Event('end'));
  }

  /** stop capturing and don't emit any transcibed audio */
  public abort() {
    if (this.listening) {
      const micStream = MicStream.getInstance();
      if (micStream) {
        if (micStream.removeAllListeners) {
          micStream.removeAllListeners('data');
        }
        micStream.end();
      }
      const connection = Connection.getInstance();
      if (connection) {
        connection.close();
      }
      this.listening = false;
      this.dispatchEvent(new Event('audioend'));
      this.dispatchEvent(new Event('end'));
    }
  }

  /** dispatch transcription result */
  private emitResult(transcript: string, isFinal: boolean) {
    if (!this.continuous && isFinal && this.listening) {
      this.stop();
    }

    if (transcript && transcript.length > 1) {
      this.dispatchEvent(
        new AWSSpeechRecognitionEvent('result', [
          {
            0: {
              transcript,
              confidence: 1,
            },
            isFinal,
          },
        ])
      );
    } else {
      this.dispatchEvent(new Event('nomatch'));
    }
  }

  /** dispatch error event */
  private emitError(error: Error) {
    this.stop();
    this.dispatchEvent(new ErrorEvent('error', error));
  }

  /** dispatch events related to sound start */
  private emitSoundStart() {
    this.dispatchEvent(new ErrorEvent('speechstart'));
    this.dispatchEvent(new ErrorEvent('soundstart'));
  }

  /** dispatch events realated to sound end */
  private emitSoundEnd() {
    this.dispatchEvent(new ErrorEvent('speechend'));
    this.dispatchEvent(new ErrorEvent('soundend'));
  }

  /** authenticate and connect to AWS Transcribe */
  private async establishConnection(mediaStream: MediaStream) {
    try {
      const { signedUrlProvider } = this.config;

      if (signedUrlProvider) {
        // if a custom credential provider is provided, use it to get credentials
        const signedUrl = await signedUrlProvider();
        if (!signedUrl) {
          throw new Error('Invalid signedUrl provided by the signed url provider');
        }

        Connection.setUrl(signedUrl);
      }

      MicStream.setStream(mediaStream);
      this.streamAudioToWebSocket();

      this.listening = true;
      this.dispatchEvent(new Event('audiostart'));
    } catch (err) {
      if (err instanceof Error) {
        this.emitError(err);
      }
    }
  }

  /** handle streaming received audio buffer to AWS transcribe */
  private streamAudioToWebSocket() {
    try {
      const connection = Connection.getInstance();
      if (!connection) {
        console.error('no usable connection');
        return;
      }
      connection.onopen = () => {
        const micStream = MicStream.getInstance();
        if (!micStream) {
          console.error('no usable stream');
          return;
        }
        this.emitSoundStart();
        // Remove any previous listeners to avoid leaks
        if (micStream.removeAllListeners) {
          micStream.removeAllListeners('data');
        }
        // when audio is received from the mic stream, send it AWS
        micStream.on('data', (audioChunk: Buffer) => {
          // Only send if connection is open
          if (Connection.isActive() && connection.readyState === 1) {
            const binary = convertAudioToBinaryMessage(audioChunk, this.config.sampleRate);
            connection.send(binary as unknown as ArrayBuffer);
          }
        });
        this.handleSocketMessages();
      };
    } catch (error) {
      if (error instanceof Error) {
        this.emitError(error);
      }
    }
  }

  /** handle websocket responses */
  private handleSocketMessages() {
    const eventStreamMarshaller = new EventStreamMarshaller(toUtf8, fromUtf8);
    const stringEncode = (data: ArrayBufferLike) => new TextDecoder('utf-8').decode(data);

    // convert the binary event stream message to JSON
    type ParseMessageBody = (response: { body: ArrayBufferLike }) => AWSTranscribeResponse;
    const parseMessageBody: ParseMessageBody = createPipe(prop('body'), stringEncode, JSON.parse.bind(JSON));

    const connection = Connection.getInstance();
    if (connection) {
      connection.onmessage = createPipe(
        prop('data'),
        Buffer.from,
        (buffer: Buffer) => eventStreamMarshaller.unmarshall(buffer),
        ifElse(
          pathEq(['headers', ':message-type', 'value'], 'event'),
          // valid response
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          createPipe(parseMessageBody, pathOr([], ['Transcript', 'Results']), (results: any[]) => {
            if (
              Array.isArray(results) &&
              results.length > 0 &&
              results[0].Alternatives &&
              Array.isArray(results[0].Alternatives) &&
              results[0].Alternatives.length > 0
            ) {
              const transcript = decodeURIComponent(results[0].Alternatives[0].Transcript || '');
              const isPartial = results[0].IsPartial;
              this.emitResult(transcript, !isPartial);
            }
          }),
          // error response
          createPipe(parseMessageBody, prop('Message'), console.error)
        )
      );
    }
  }

  // stub some unimplemented props/methods

  private _interimResults = false;
  set interimResults(val) {
    this._interimResults = !!val;
  }

  get interimResults() {
    return !!this._interimResults;
  }

  set maxAlternatives(_) {
    console.warn('`maxAlternatives` is not yet implemented in the AWS polyfill');
  }

  get maxAlternatives() {
    return 1;
  }

  set grammars(_) {
    console.warn('`grammars` is not yet implemented in the AWS polyfill');
  }

  get grammars() {
    console.warn('`grammars` is not yet implemented in the AWS polyfill');
    return SpeechGrammar ? new SpeechGrammarList() : ([] as unknown as SpeechGrammarList);
  }

  // proxy event listeners
  set onaudiostart(fn: ListenerCallback) {
    this.addEventListener('audiostart', fn);
  }

  set onaudioend(fn: ListenerCallback) {
    this.addEventListener('audioend', fn);
  }

  set onend(fn: ListenerCallback) {
    this.addEventListener('end', fn);
  }

  set onerror(fn: ListenerCallback) {
    this.addEventListener('error', fn);
  }

  set onnomatch(fn: ListenerCallback) {
    this.addEventListener('nomatch', fn);
  }

  set onresult(fn: ListenerCallback) {
    this.addEventListener('result', fn);
  }

  set onsoundstart(fn: ListenerCallback) {
    this.addEventListener('soundstart', fn);
  }

  set onsoundend(fn: ListenerCallback) {
    this.addEventListener('soundend', fn);
  }

  set onspeechstart(fn: ListenerCallback) {
    this.addEventListener('speechstart', fn);
  }

  set onspeechend(fn: ListenerCallback) {
    this.addEventListener('speechend', fn);
  }

  set onstart(fn: ListenerCallback) {
    this.addEventListener('start', fn);
  }
}

export const createAwsSpeechServicesPonyfill = (config: configArgs) => {
  return AWSRecognizer.create(config);
};

export default AWSRecognizer;
